import { ChevronLeft, ChevronRight, Search, X } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { t } from '@/i18n'

export interface ListFilterState {
  from: string
  to: string
  search: string
}

export const NO_FILTERS: ListFilterState = { from: '', to: '', search: '' }

interface ListFiltersProps {
  value: ListFilterState
  onChange: (next: ListFilterState) => void
  /** Placeholder for the search box — say what can be typed into it, not "search". */
  searchHint: string
  /** Off for a list nothing is dated by, such as the product catalogue. */
  showDates?: boolean
  /** Off where there is no number or name to quote — a write-off, for instance. */
  showSearch?: boolean
}

/**
 * The date range and search box above a list.
 *
 * These matter more than the paging under them. Nobody walks back through four thousand receipts to
 * find one: they know roughly when it was, or they are holding a slip with the number printed on it.
 * Paging is what makes the rest reachable; this is what makes it unnecessary.
 */
export function ListFilters({
  value,
  onChange,
  searchHint,
  showDates = true,
  showSearch = true,
}: ListFiltersProps) {
  const dirty = value.from !== '' || value.to !== '' || value.search !== ''

  return (
    <div className="mb-4 flex flex-wrap items-end gap-3">
      {showDates && (
        <>
          <Input
            type="date"
            label={t('From')}
            value={value.from}
            max={value.to || undefined}
            onChange={(event) => onChange({ ...value, from: event.target.value })}
            wrapperClassName="w-40"
          />
          <Input
            type="date"
            label={t('To')}
            value={value.to}
            min={value.from || undefined}
            onChange={(event) => onChange({ ...value, to: event.target.value })}
            wrapperClassName="w-40"
          />
        </>
      )}

      {showSearch && (
      <Input
        label={t('Search')}
        placeholder={searchHint}
        value={value.search}
        onChange={(event) => onChange({ ...value, search: event.target.value })}
        wrapperClassName="min-w-52 flex-1"
        leading={<Search className="h-4 w-4" />}
      />
      )}

      {dirty && (
        <Button variant="ghost" size="sm" onClick={() => onChange(NO_FILTERS)}>
          <X className="me-1 h-4 w-4" />
          {t('Clear')}
        </Button>
      )}
    </div>
  )
}

interface PagerProps {
  /** The list being paged. Passed whole so a screen cannot wire half of it up. */
  list: {
    page: readonly unknown[]
    total: number | null
    from: number
    hasPrevious: boolean
    hasNext: boolean
    isFetching: boolean
    previous: () => void
    next: () => void
  }
}

/**
 * Previous and next for a list the server hands over a page at a time.
 *
 * The same shape as the pager under the report tables, because a shopkeeper should not have to
 * learn that some lists grow when you press a button and others turn over. It always says which
 * rows are on screen and how many there are: a list that stops at twenty looks exactly like a shop
 * that had twenty.
 */
export function Pager({ list }: PagerProps) {
  if (list.total === 0 || (!list.hasPrevious && !list.hasNext)) return null

  const to = list.from + list.page.length - 1

  return (
    <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
      <Button
        variant="secondary"
        size="sm"
        onClick={list.previous}
        disabled={!list.hasPrevious}
        aria-label={t('Previous page')}
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>

      <span className="tabular text-sm text-[var(--text-secondary)]">
        {list.total === null
          ? t('{from}–{to}', { from: list.from, to })
          : t('{from}–{to} of {total}', {
              from: list.from.toLocaleString(),
              to: to.toLocaleString(),
              total: list.total.toLocaleString(),
            })}
      </span>

      <Button
        variant="secondary"
        size="sm"
        onClick={list.next}
        disabled={!list.hasNext || list.isFetching}
        aria-label={t('Next page')}
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  )
}
